import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {Config, ConfigService, Mode, SUPPORTED_MODES} from './config.service';

/**
 * Zentrale Zugriffsschicht auf den aktiven Portal-Modus der Shell.
 *
 * Hintergrund (SHEL-57): Die Shell ist mandantenfähig - dasselbe Frontend bedient
 * sowohl das Just-Farming-Portal ('default') als auch das Gewerbeportal
 * ('gewerbeportal'). Der Modus stammt aus der Backend-Konfiguration
 * (`ConfigDTO.mode`, geladen über `/api/v1/config`).
 *
 * Dieser Service kapselt die Normalisierung des Portal-Modus (siehe
 * `normalizeMode`) und stellt abgeleitete Portal-Informationen bereit:
 * Sidebar-Logo-Pfad (SHEL-61), Anzeigenamen für Fließtexte (SHEL-64) und die
 * Gewerbeportal-Erkennung für portalabhängige Funktionen (SHEL-81, SHEL-66).
 */
@Injectable()
export class PortalConfigService {

  constructor(
    private readonly configService: ConfigService,
  ) {
  }

  /**
   * Fällt defensiv auf 'default' zurück, falls die Konfiguration (noch) nicht
   * geladen ist oder einen unbekannten Wert liefert (siehe `normalizeMode`).
   */
  get mode(): Mode {
    return this.normalizeMode(this.configService.config?.mode);
  }

  /**
   * Stützt sich auf den normalisierten `mode` - unbekannte oder fehlende Modi
   * zählen damit als Just Farming.
   */
  get isGewerbeportal(): boolean {
    return this.mode === 'gewerbeportal';
  }

  /**
   * Reaktiver Strom des normalisierten Portal-Modus.
   *
   * Wird benötigt, wo sich der Modus zur Laufzeit ändern kann (z.B. über
   * `ConfigService.setModeOverride()` bzw. den `?shellMode`-Test-Override) oder
   * wo Templates per AsyncPipe gebunden werden. Emittiert bei jeder neu
   * geladenen Konfiguration.
   */
  get mode$(): Observable<Mode> {
    return this.configService.loaded$.pipe(
      map((config: Config) => this.normalizeMode(config.mode)),
    );
  }

  /**
   * Pfad zum Portal-Logo für die Hauptnavigation (Sidebar) je Modus (SHEL-61).
   *
   * Begründung: Die Logos sind statische Assets im Frontend - sie sind Teil des
   * ausgelieferten Bundles und stehen damit ohne zusätzlichen Backend-Aufruf
   * sofort zur Verfügung. Für das Gewerbeportal wurde gemäß SHEL-61/SHEL-62 das
   * quadratische Signet auf weißem Grund gewählt, da die Sidebar hell ist.
   */
  private static readonly SIDEBAR_LOGOS: Record<Mode, string> = {
    'default': 'assets/images/logo-without-text.svg',
    'gewerbeportal': 'assets/images/gewerbeportal/logo-without-text.svg',
  };

  /**
   * Unbekannte Modi fallen über den normalisierten `mode`-Getter automatisch
   * auf das Just-Farming-Logo zurück.
   */
  get sidebarLogoPath(): string {
    return PortalConfigService.SIDEBAR_LOGOS[this.mode];
  }

  /**
   * Anzeigename des Portals für Fließtexte, z.B. im Einwilligungs-Dialog zu
   * Nutzungsdaten (SHEL-64).
   *
   * Gemäß fachlicher Klärung in SHEL-64 wird im Gewerbeportal ausschließlich der
   * PORTALNAME ersetzt ("Just Farming Portal" -> "Gewerbeportal");
   * produktunabhängige Begriffe wie der "Just Farming Benutzerservice" bleiben
   * in beiden Portalen unverändert.
   */
  get portalDisplayName(): string {
    return this.isGewerbeportal ? 'Gewerbeportal' : 'Just Farming Portal';
  }

  /**
   * Genitiv-Form des Portalnamens für Formulierungen wie "des ... Portals"
   * (SHEL-64), z.B. "des Gewerbeportals" bzw. "des Just Farming Portals".
   */
  get portalDisplayNameGenitive(): string {
    return this.isGewerbeportal ? 'Gewerbeportals' : 'Just Farming Portals';
  }

  /**
   * Normalisiert einen beliebigen Roh-Wert auf einen bekannten `Mode`.
   *
   * Wichtig: TypeScript-Typen existieren zur Laufzeit nicht - das Backend kann
   * laut API-Vertrag (`ConfigDTO.mode`) künftig weitere Modus-Werte liefern.
   * Unbekannte oder fehlende Werte werden hier bewusst auf 'default' abgebildet,
   * damit neue Portale ohne Frontend-Anpassung sicher wie Just Farming
   * dargestellt werden (Vorgabe der API-Dokumentation). Die Normalisierung ist
   * bewusst hier gebündelt.
   */
  private normalizeMode(value: unknown): Mode {
    return SUPPORTED_MODES.includes(value as Mode) ? (value as Mode) : 'default';
  }
}
